/**
 * CashbackConfig — Cashback utilisateur par tier
 *
 * Règle métier (v1.12.144) :
 * 1. Outfit touche une commission du partenaire (ex: cashmere 10% du prix).
 * 2. On reverse une % de cette commission au user, variable selon son tier :
 *      free=5%, basic=10%, premium=15%, gold=25%
 * 3. Cashback réel en € = price × commissionPartenaire × tierShare
 *
 * Exemple cashmere @ 100€ :
 *   - commission Outfit : 100 × 10% = 10 €
 *   - free  : 10 × 5%  = 0.50 €
 *   - basic : 10 × 10% = 1.00 €
 *   - premium: 10 × 15% = 1.50 €
 *   - gold  : 10 × 25% = 2.50 €
 */

/** Conversion USD → EUR approximative */
export const USD_TO_EUR = 0.92;

/**
 * % de la commission partenaire reversée au user, selon son abonnement.
 * Source de vérité (synchro avec PremiumService.CASHBACK_NEW).
 * v1.12.313 — Nouveau barème (non affiché en UI).
 */
export const TIER_CASHBACK_SHARE = {
  free:    0.10,
  basic:   0.15,
  premium: 0.25,
  gold:    0.40,
};

/**
 * Commission touchée par Outfit de la part du partenaire.
 * Si le partenaire n'est pas listé, fallback 5% (Skimlinks moyen).
 */
const PARTNER_COMMISSION = {
  // Affiliés directs
  cashmere:  0.10, // 10% négocié directement
  iqueens:   0.08, // 8% via CJ
  zalando:   0.08,
  asos:      0.08,
  hm:        0.06,
  mango:     0.06,
  zara:      0.05,
  amazon:    0.06,
  farfetch:  0.10,
  net_a_porter: 0.10,
  tous:      0.0662, // v1.12.330 — CPA TOUS 6,62% taxes incl. (verrouillage 75j)
  rakuten:   0.08, // 8% mode H/F via Awin
  suit_negozi: 0.08, // v1.12.314 — Suit Negozi via Rakuten Advertising. Taux PAR MARQUE
                     //             dans SUIT_NEGOZI_BRAND_COMMISSION ci-dessous (8/10/12%).
                     //             Fallback partner = 8% si la marque n'est pas listée.
  ann_demeulemeester: 0.04, // v1.12.324 — Ann Demeulemeester via Rakuten Advertising (MID 53588).
                            //             Contrat Rakuten : 4% sur la vente (dernier clic, action 3.1).
  coutr:     0.08,          // v1.12.336 — COUTR via Rakuten Advertising (MID 54152).
                            //             Contrat Rakuten : 8% baseline (action 12.1). Prix en USD.

  // Fallback Skimlinks (clic affilié)
  _default:  0.045,
};

/**
 * v1.12.314 — Suit Negozi : commission Rakuten Advertising VARIE par marque.
 * Source : barème publisher fourni par Suit Negozi via Rakuten dashboard.
 *
 * Les clés sont normalisées via normalizeBrand() ci-dessous (uppercase,
 * apostrophes uniformisées, espaces collapsés). Toute marque non listée tombe
 * dans le fallback partner (0.08 = 8%, le taux le plus bas du barème).
 */
const SUIT_NEGOZI_BRAND_COMMISSION = {
  // ─── 8% ─── prestige luxe (volume élevé, marge négociée)
  "ASICS": 0.08, "BALENCIAGA": 0.08, "CANADA GOOSE": 0.08, "CELINE": 0.08,
  "CHRISTIAN DIOR": 0.08, "GANNI": 0.08, "GUCCI": 0.08, "LOEWE": 0.08,
  "LOEWE X PAULA'S IBIZA": 0.08, "MIU MIU": 0.08, "MONCLER": 0.08,
  "MONCLER GENIUS": 0.08, "MONCLER GRENOBLE": 0.08, "PRADA": 0.08,
  "ROGER VIVIER": 0.08, "SALOMON": 0.08, "STONE ISLAND": 0.08, "TOTEME": 0.08,

  // ─── 10% ─── marques contemporaines / accessible-luxe
  "ALAIA": 0.10, "AMI PARIS": 0.10, "AMIRI": 0.10, "BARBOUR": 0.10,
  "BOTTEGA VENETA": 0.10, "BURBERRY": 0.10, "CARHARTT WIP": 0.10,
  "CHURCH'S": 0.10, "FENDI": 0.10, "GOLDEN GOOSE DELUXE BRAND": 0.10,
  "JACQUEMUS": 0.10, "KHAITE": 0.10, "LEMAIRE": 0.10, "LOEWE PERFUMES": 0.10,
  "LOUBOUTIN": 0.10, "MAISON MARGIELA": 0.10, "MAX MARA": 0.10,
  "MCQUEEN": 0.10, "OFF-WHITE": 0.10, "REPRESENT": 0.10, "RICK OWENS": 0.10,
  "SAINT LAURENT": 0.10, "SMAXMARA": 0.10, "STELLA MCCARTNEY": 0.10,

  // ─── 12% ─── marques mid-luxe / niche / spécialisées
  "44 LABELGROUP": 0.12, "APC": 0.12, "BATAKOVIC": 0.12,
  "BRUNELLO CUCINELLI": 0.12, "BUTTERO": 0.12, "CASABLANCA": 0.12,
  "CHLOE'": 0.12, "COPERNI": 0.12, "COURREGES": 0.12, "CP COMPANY": 0.12,
  "DRIES VAN NOTEN": 0.12, "ETRO": 0.12, "FACE.HIDE": 0.12,
  "FAY ARCHIVE": 0.12, "FERRAGAMO": 0.12, "FINAMORE": 0.12, "FRAY": 0.12,
  "GIANVITO ROSSI": 0.12, "GIORGIO ARMANI": 0.12, "GIVENCHY": 0.12,
  "HEVO": 0.12, "INCOTEX": 0.12, "J.W.ANDERSON": 0.12, "JIL SANDER": 0.12,
  "JIMMY CHOO": 0.12, "KITON": 0.12, "LABSOLUE": 0.12, "LARDINI": 0.12,
  "LOIS": 0.12, "MAGLIANO": 0.12, "MAX MARA PURE": 0.12,
  "MAX MARA THE CUBE": 0.12, "MAXMARA ATELIER": 0.12,
  "MAXMARA PIANOFORTE": 0.12, "MOORER": 0.12, "NICKY": 0.12,
  "NINE IN THE MORNING": 0.12, "NOME": 0.12, "NOME X XACUS": 0.12,
  "ORTO PARISI": 0.12, "PALM ANGELS": 0.12, "PAUL&SHARK": 0.12,
  "PHILIPP PLEIN": 0.12, "PT TORINO": 0.12, "RHUDE": 0.12,
  "ROBERTO COLLINA": 0.12, "SACAI": 0.12, "SALVATORE SANTORO": 0.12,
  "SEAFARER": 0.12, "SELF-PORTRAIT": 0.12, "SPORTMAX": 0.12,
  "TAGLIATORE": 0.12, "TOD'S": 0.12, "TOM FORD": 0.12,
  "TOM FORD UNDERWEAR": 0.12, "TRANSIT": 0.12, "VALENTINO GARAVANI": 0.12,
  "WOOLRICH": 0.12, "ZANONE": 0.12, "ZEGNA": 0.12, "ZIMMERMANN": 0.12,
};

/**
 * Normalise un nom de marque pour matching (uppercase + apostrophes
 * uniformisées + espaces collapsés). Permet de matcher "Tod's", "TOD'S",
 * "Tod`s", "tod's" etc. avec la même clé.
 */
function normalizeBrand(b) {
  return String(b || '')
    .toUpperCase()
    .replace(/[`´'‘’ʼ]/g, "'") // unifier toutes apostrophes / accents
    .replace(/\s+/g, ' ')
    .trim();
}

/** Commission partenaire (rate) sur ce produit. */
export function getPartnerCommissionRate(product) {
  if (!product) return PARTNER_COMMISSION._default;
  const partner = String(product.partner || '').toLowerCase();
  // v1.12.314 — Suit Negozi : taux PAR MARQUE
  if (partner === 'suit_negozi') {
    const brandKey = normalizeBrand(product.brand);
    if (SUIT_NEGOZI_BRAND_COMMISSION[brandKey] != null) {
      return SUIT_NEGOZI_BRAND_COMMISSION[brandKey];
    }
    return PARTNER_COMMISSION.suit_negozi; // fallback Suit Negozi = 8%
  }
  return PARTNER_COMMISSION[partner] || PARTNER_COMMISSION._default;
}

/** Commission partenaire en € sur ce produit. */
export function getPartnerCommissionAmount(product) {
  const price = Number(product?.price) || 0;
  return Math.round(price * getPartnerCommissionRate(product) * 100) / 100;
}

/** Part (%) reversée au user pour son tier (0..1). */
export function getTierShare(tier) {
  const t = String(tier || 'free').toLowerCase();
  return TIER_CASHBACK_SHARE[t] != null ? TIER_CASHBACK_SHARE[t] : TIER_CASHBACK_SHARE.free;
}

/**
 * Détail complet du cashback pour un produit + tier.
 * @returns {{
 *   commissionRate:number,    // 0.10
 *   commissionEur:number,     // 10.00
 *   tierShare:number,         // 0.25
 *   tierSharePct:number,      // 25
 *   cashbackEur:number,       // 2.50
 *   effectiveRate:number,     // 0.025  (commission × share)
 *   effectivePct:number,      // 2.5    (display "≈ 2,5%")
 * }}
 */
export function getCashbackBreakdown(product, tier) {
  const commissionRate  = getPartnerCommissionRate(product);
  const commissionEur   = getPartnerCommissionAmount(product);
  const tierShare       = getTierShare(tier);
  const cashbackEur     = Math.round(commissionEur * tierShare * 100) / 100;
  const effectiveRate   = commissionRate * tierShare;
  return {
    commissionRate,
    commissionEur,
    tierShare,
    tierSharePct: Math.round(tierShare * 100),
    cashbackEur,
    effectiveRate,
    effectivePct: Math.round(effectiveRate * 1000) / 10, // 1 décimale
  };
}

/**
 * @deprecated Utiliser getCashbackBreakdown(product, tier) à la place.
 * Conservé pour compat (anciens consommateurs sans tier — assume free).
 */
export function getCashbackRate(product, tier) {
  return getCashbackBreakdown(product, tier).effectiveRate;
}

/** Montant € — accepte tier optionnel (default free). */
export function getCashbackAmount(product, tier) {
  return getCashbackBreakdown(product, tier).cashbackEur;
}

/**
 * % d'affichage sur les cards (compact).
 * Avant v1.12.144 : pourcentage du PRIX (ex: 1%).
 * Maintenant : on retourne le % de la COMMISSION partenaire (ex: 25 pour gold)
 * — c'est plus différenciant pour les tiers et plus parlant en preview.
 */
export function getCashbackPercent(product, tier) {
  return getCashbackBreakdown(product, tier).tierSharePct;
}
